import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, PlayCircle } from 'lucide-react';
import { ProgressBar } from './components/ui/ProgressBar';
import { SlideTitle } from './components/SlideTitle';
import { SlideLifecycle } from './components/SlideLifecycle';
import { SlideMarketSize } from './components/SlideMarketSize';
import { SlideCalculator } from './components/SlideCalculator';
import { SlideBottleneck } from './components/SlideBottleneck';
import { SlideStrategy } from './components/SlideStrategy';
import { SlideWhatSnakkerDoes } from './components/SlideWhatSnakkerDoes';
import { SlideCampaignExample } from './components/SlideCampaignExample';
import { SlideFastCalculator } from './components/SlideFastCalculator';
import { SlideCTA } from './components/SlideCTA';
import { SlideCentralizeToLocalize } from './components/SlideCentralizeToLocalize';
import { SlideCampaignEngine } from './components/SlideCampaignEngine';
import { SlideControlTower } from './components/SlideControlTower';
import { SlideProposition } from './components/SlideProposition';
import { SlideSnakAnatomy } from './components/SlideSnakAnatomy';
import { SlideSnakLifecycleFit } from './components/SlideSnakLifecycleFit';
import { SlideWhereWeFit } from './components/SlideWhereWeFit';
import { SlideCampaignEngineResponsibility } from './components/SlideCampaignEngineResponsibility';
import { SlideExecutionRationale } from './components/SlideExecutionRationale';
import { SlideGovernance } from './components/SlideGovernance';
import { SlideObjections } from './components/SlideObjections';
import { SlideCompleteProposition } from './components/SlideCompleteProposition';
import { SlideCommercialModel } from './components/SlideCommercialModel';
import { ExecutiveSummary } from './components/ExecutiveSummary';
import { SnakPlayer } from './components/SnakPlayer';
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [snakOpen, setSnakOpen] = useState(false);
  const totalSlides = 23;
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        return <SlideTitle onContinue={nextSlide} data-id="element-0" data-mp="yhepoy2191j2" />;
      case 1:
        return <SlideLifecycle onContinue={nextSlide} data-id="element-1" data-mp="13fpz57of1a0r" />;
      case 2:
        return <SlideMarketSize onContinue={nextSlide} data-id="element-2" data-mp="1r4ip8nicwnng" />;
      case 3:
        return <SlideCalculator onContinue={nextSlide} data-id="element-3" data-mp="1r266w98osb9v" />;
      case 4:
        return <SlideBottleneck onContinue={nextSlide} data-id="element-4" data-mp="1w20x0akn0wtv" />;
      case 5:
        return <SlideProposition onContinue={nextSlide} data-id="element-5" data-mp="1i4ce38a4rzrg" />;
      case 6:
        return <SlideSnakAnatomy onContinue={nextSlide} data-id="element-6" data-mp="1m560tybu78ir" />;
      case 7:
        return <SlideSnakLifecycleFit onContinue={nextSlide} data-id="element-7" data-mp="1m3cwipop50dx" />;
      case 8:
        return <SlideCentralizeToLocalize onContinue={nextSlide} data-id="element-8" data-mp="6nmggdjrwg9g" />;
      case 9:
        return <SlideCampaignEngine onContinue={nextSlide} data-id="element-9" data-mp="aq99uiw3e98r" />;
      case 10:
        return <SlideCampaignEngineResponsibility onContinue={nextSlide} data-id="element-10" data-mp="qwvqcatjkh4s" />;
      case 11:
        return <SlideExecutionRationale onContinue={nextSlide} data-id="element-11" data-mp="m6fdosxjozjh" />;
      case 12:
        return <SlideControlTower onContinue={nextSlide} data-id="element-12" data-mp="m4ccdcqxzjzg" />;
      case 13:
        return <SlideStrategy onContinue={nextSlide} data-id="element-13" data-mp="h7vnuhftz3lu" />;
      case 14:
        return <SlideWhereWeFit onContinue={nextSlide} data-id="element-14" data-mp="c90w2pzvy4pl" />;
      case 15:
        return <SlideWhatSnakkerDoes onContinue={nextSlide} data-id="element-15" data-mp="19vztx42bdip2" />;
      case 16:
        return <SlideCampaignExample onContinue={nextSlide} data-id="element-16" data-mp="19tx70swgk5ok" />;
      case 17:
        return <SlideGovernance onContinue={nextSlide} data-id="element-17" data-mp="14xg8r5vjoaxr" />;
      case 18:
        return <SlideFastCalculator onContinue={nextSlide} data-id="element-18" data-mp="zx29ac80e9bc" />;
      case 19:
        return <SlideCommercialModel onContinue={nextSlide} data-id="element-19" data-mp="vwie3e6zxfru" />;
      case 20:
        return <SlideCompleteProposition onContinue={nextSlide} data-id="element-20" data-mp="vybe4an3t1qv" />;
      case 21:
        return <SlideObjections onContinue={nextSlide} data-id="element-21" data-mp="3kpdvoovlsam9" />;
      case 22:
        return <SlideCTA onContinue={nextSlide} data-id="element-22" data-mp="3glefy8i5dfty" />;
      default:
        return null;
    }
  };
  return <div className="w-full h-screen bg-navy-800 overflow-hidden relative font-sans text-slate-900" data-id="element-23" data-mp="1w5gecryhzy4s">
      <ProgressBar current={currentSlide} total={totalSlides} onBack={prevSlide} onSkip={nextSlide} data-id="element-24" data-mp="2071zt1p3lkql" />

      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-25" data-mp="2530iq9rnqajm">
        <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-26" data-mp="29wlia68wfckx">
          {renderSlide()}
        </motion.div>
      </AnimatePresence>

      <button onClick={() => setSummaryOpen(true)} className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#6C2BD9] text-sm font-semibold shadow-lg shadow-[#6C2BD9]/30 hover:bg-[#5A23B8] hover:shadow-xl hover:shadow-[#6C2BD9]/40 hover:-translate-y-0.5 transition-all" aria-label="Open executive summary" style={{
      color: '#ffffff'
    }} data-id="element-27" data-mp="ygoi1lvm168e">
        <Sparkles className="w-4 h-4" style={{
        color: '#ffffff'
      }} data-id="element-28" data-mp="25sw8k2mrf5lj" />
        <span style={{
        color: '#ffffff'
      }} data-id="element-29" data-mp="2kp55uyn3b8vb">
          Executive Summary
        </span>
      </button>

      <button onClick={() => setSnakOpen(true)} className="fixed bottom-6 left-[15.5rem] z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-navy-900 border border-[#6C2BD9]/30 text-sm font-semibold text-[#6C2BD9] shadow-lg shadow-slate-900/5 hover:bg-[#6C2BD9]/[0.06] hover:-translate-y-0.5 transition-all" aria-label="Play an example Snak" data-id="element-30" data-mp="2fpipwhfvneit">
        <PlayCircle className="w-4 h-4" data-id="element-31" data-mp="2yjny4h9bkvzw" />
        <span data-id="element-32" data-mp="3i33ev9r44ata">Play a Snak</span>
      </button>

      <ExecutiveSummary open={summaryOpen} onClose={() => setSummaryOpen(false)} data-id="element-33" data-mp="3i4ehh68tncit" />

      <SnakPlayer open={snakOpen} onClose={() => setSnakOpen(false)} data-id="element-34" data-mp="3n1y52wret1nf" />
    </div>;
}